"use client";

import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";

import {
  addKeyGrantFn,
  removeKeyGrantsByGranteeFn,
  getSharingContextFn,
  getActiveCoachUserIdsFn,
} from "~/lib/functions/encrypted-data";
import { getLinkedCoachTargetsFn } from "~/lib/functions/goal-link";
import { userSharedStatusQueryOptions } from "~/lib/queries/userSharedQuery";
import type { LifeDesignSharedResourceType } from "~/lib/schemas/key-grants";

import {
  base64UrlToBuffer,
  unwrapDek,
  wrapDek,
  type WrappedDek,
} from "./crypto";
import type { KeyManager } from "./key-manager";
import type { ShareTarget } from "./shared-grants";

/** The owner's own grant for a single resource, as returned by getSharingContextFn. */
interface OwnGrant {
  resourceType: LifeDesignSharedResourceType;
  resourceId: string;
  encryptedDek: string;
}

/** Unwrap the owner's DEK from their own key grant using their private key. */
async function unwrapOwnDek(
  grant: OwnGrant,
  privateKey: CryptoKey,
): Promise<CryptoKey> {
  const wrappedDek: WrappedDek = JSON.parse(grant.encryptedDek);
  const ephemeralPubKeyBytes = base64UrlToBuffer(wrappedDek.ephemeralPublicKey);
  const ephemeralPubKey = await crypto.subtle.importKey(
    "raw",
    ephemeralPubKeyBytes,
    { name: "ECDH", namedCurve: "P-256" },
    true,
    [],
  );
  return unwrapDek(wrappedDek, ephemeralPubKey, privateKey);
}

/** Re-wrap an unwrapped DEK for a coach and persist the grant. */
async function grantDekToCoach(
  dek: CryptoKey,
  coach: ShareTarget,
  resourceType: LifeDesignSharedResourceType,
  resourceId: string,
): Promise<void> {
  const coachPubKeyBytes = base64UrlToBuffer(coach.publicKey);
  const coachPublicKey = await crypto.subtle.importKey(
    "raw",
    coachPubKeyBytes,
    { name: "ECDH", namedCurve: "P-256" },
    true,
    [],
  );
  const coachWrappedDek = await wrapDek(dek, coachPublicKey);
  await addKeyGrantFn({
    data: {
      resourceType,
      resourceId,
      userId: coach.userId,
      encryptedDek: JSON.stringify(coachWrappedDek),
    },
  });
}

interface ShareWithCoachesParams {
  /** Resource types to share/unshare (e.g., ["goal", "overallVision"]) */
  resourceTypes: readonly LifeDesignSharedResourceType[];
}

/**
 * Hook to manage key grants for coach sharing.
 *
 * When sharing is enabled, creates coach key grants for coaches who have encryption keys.
 * When sharing is disabled, removes all coach key grants for the specified resource types.
 */
export function useShareWithCoaches(
  keyManager: KeyManager | null,
  { resourceTypes }: ShareWithCoachesParams,
): {
  /** Grant access to all coaches with encryption keys for the given resource types. */
  grantCoachAccess: () => Promise<void>;
  /** Revoke all coach access for the given resource types. */
  revokeCoachAccess: () => Promise<void>;
} {
  const grantCoachAccess = useCallback(async () => {
    if (!keyManager) return;

    // Get coaches with encryption keys (returns { coachId, publicKey, userId })
    const coaches = await getActiveCoachUserIdsFn({ data: {} });
    if (coaches.length === 0) return;

    // Get the user's own key grants for the specified resource types
    const { ownGrants } = await getSharingContextFn({
      data: { resourceTypes: [...resourceTypes] },
    });
    if (ownGrants.length === 0) return;

    // For each resource, unwrap the DEK and re-wrap for each coach
    const privateKey = keyManager.getPrivateKey();

    await Promise.all(
      ownGrants.map(async (grant) => {
        const dek = await unwrapOwnDek(grant, privateKey);
        await Promise.all(
          coaches.map((coach) =>
            grantDekToCoach(dek, coach, grant.resourceType, grant.resourceId),
          ),
        );
      }),
    );
  }, [keyManager, resourceTypes]);

  const revokeCoachAccess = useCallback(async () => {
    // Get coaches with encryption keys
    const coaches = await getActiveCoachUserIdsFn({ data: {} });
    if (coaches.length === 0) return;

    // Remove all key grants for each coach (by userId) across the specified resource types
    await Promise.all(
      coaches.flatMap((coach) =>
        resourceTypes.map((resourceType) =>
          removeKeyGrantsByGranteeFn({
            data: { resourceType, granteeUserId: coach.userId },
          }),
        ),
      ),
    );
  }, [resourceTypes]);

  return { grantCoachAccess, revokeCoachAccess };
}

/**
 * Give every coach the keys of records whose keys were written without
 * theirs. The owner's encryption of their older plain-text Life Design steps
 * does this; a save sends its coaches' keys with the record instead.
 *
 * Exported as a plain function (taking the owner's private key) so the logic is
 * testable without the React hook wrapper.
 */
export async function grantResourcesToCoaches(
  privateKey: CryptoKey | null,
  {
    resourceType,
    resourceIds,
  }: {
    resourceType: LifeDesignSharedResourceType;
    resourceIds: readonly string[];
  },
): Promise<void> {
  if (!privateKey || resourceIds.length === 0) return;

  const coaches = await getActiveCoachUserIdsFn({ data: {} });
  if (coaches.length === 0) return;

  const { ownGrants } = await getSharingContextFn({
    data: { resourceTypes: [resourceType] },
  });
  const wanted = new Set(resourceIds);
  await Promise.all(
    ownGrants
      .filter((grant) => wanted.has(grant.resourceId))
      .map(async (grant) => {
        const dek = await unwrapOwnDek(grant, privateKey);
        await Promise.all(
          coaches.map((coach) =>
            grantDekToCoach(dek, coach, resourceType, grant.resourceId),
          ),
        );
      }),
  );
}

/** Which Life Design switch covers a record: the vision page's or the goals page's. */
export type LifeDesignShareArea = "vision" | "goals";

/**
 * The coaches a Life Design save must give its new key to. A save reads the
 * sharing status fresh, before it encrypts, because the switch may have
 * changed since the page loaded, for example in another tab.
 */
export function useCoachShareTargets(): (
  area: LifeDesignShareArea,
) => Promise<ShareTarget[]> {
  const queryClient = useQueryClient();
  return useCallback(
    async (area) => {
      const status = await queryClient.fetchQuery({
        ...userSharedStatusQueryOptions(),
        staleTime: 0,
      });
      const shared =
        area === "vision"
          ? status.isSharedOverallViewWithCoach
          : status.isSharedGoalsWithCoach;
      if (!shared) return [];
      return getActiveCoachUserIdsFn({ data: {} });
    },
    [queryClient],
  );
}

/**
 * The coaches a save of this Life Design goal, or of one of its steps, gives
 * its new key to: the coaches the goals are shared with, and the coach of
 * every program the goal is linked to, who keeps reading it either way.
 */
export function useGoalShareTargets(): (
  goalId: string,
) => Promise<ShareTarget[]> {
  const coachShareTargets = useCoachShareTargets();
  return useCallback(
    async (goalId) => {
      const [shared, linked] = await Promise.all([
        coachShareTargets("goals"),
        getLinkedCoachTargetsFn({ data: { goalId } }),
      ]);
      const byUser = new Map(
        [...shared, ...linked].map((target) => [target.userId, target]),
      );
      return [...byUser.values()];
    },
    [coachShareTargets],
  );
}
